/**
 * Local Delivery - API fetch for planners.dand3.com
 */
(function () {
  'use strict';

  var API_BASE = 'https://planners.dand3.com';
  // HF tenant isolation: the store key is hard-pinned. Never adopt the WF
  // runtime-fallback pattern here (see wf-hf attribution tenant-isolation rules).
  var STORE_KEY = 'HF';
  var ATTRIBUTION_SESSION_KEY = 'wf_attr_session_v1';
  var CART_FORM_SESSION_KEY = 'wf_ld_cart_form_session_v1';
  var PUBLIC_CONFIG_CACHE_VERSION = 1;
  var PUBLIC_CONFIG_CACHE_TTL_MS = 4 * 60 * 60 * 1000;
  var PUBLIC_CONFIG_ATTEMPT_TIMEOUTS_MS = [2500, 2500];
  var PUBLIC_CONFIG_CACHED_TIMEOUT_MS = 2000;
  var PUBLIC_CONFIG_RETRY_DELAY_MS = 200;
  var READ_TIMEOUT_MS = 8000;
  var CHECKOUT_TIMEOUT_MS = 25000;
  var lastPublicConfigResult = null;

  function normalizeStoreKey(value) {
    var key = String(value || '').trim().toUpperCase();
    return /^[A-Z0-9_-]+$/.test(key) ? key : '';
  }

  function getRuntimeStoreKeyCandidate() {
    if (window.WF_LD_RUNTIME && window.WF_LD_RUNTIME.storeKey) return window.WF_LD_RUNTIME.storeKey;
    if (window.WF_LD_STORE_KEY) return window.WF_LD_STORE_KEY;
    return '';
  }

  function getStoreKey() {
    var runtimeKey = normalizeStoreKey(getRuntimeStoreKeyCandidate());
    return runtimeKey === STORE_KEY ? runtimeKey : STORE_KEY;
  }

  function setStoreKey(nextKey) {
    var normalized = normalizeStoreKey(nextKey);
    if (normalized !== STORE_KEY) normalized = STORE_KEY;
    window.WF_LD_RUNTIME = window.WF_LD_RUNTIME || {};
    window.WF_LD_RUNTIME.storeKey = normalized;
    return normalized;
  }

  function buildDeliveryUrl(pathAndQuery) {
    return API_BASE + '/api/delivery/' + encodeURIComponent(getStoreKey()) + pathAndQuery;
  }

  function nowMs() {
    try {
      if (window.performance && typeof window.performance.now === 'function') return window.performance.now();
    } catch (_) {}
    return Date.now();
  }

  function setLastPublicConfigResult(result) {
    lastPublicConfigResult = result || null;
    return lastPublicConfigResult;
  }

  function getLastPublicConfigResult() {
    if (!lastPublicConfigResult) return null;
    var copy = {};
    Object.keys(lastPublicConfigResult).forEach(function (key) {
      copy[key] = lastPublicConfigResult[key];
    });
    return copy;
  }

  function elapsedSince(startedAt) {
    return Math.max(0, Math.round(nowMs() - startedAt));
  }

  function describeFetchError(err) {
    if (!err) return 'Unknown fetch error';
    if (err && err.name && err.message) return String(err.name + ': ' + err.message).slice(0, 500);
    if (err && err.message) return String(err.message).slice(0, 500);
    return String(err).slice(0, 500);
  }

  function getLocalStorage() {
    try {
      if (window.localStorage) return window.localStorage;
    } catch (_) {}
    try {
      return localStorage;
    } catch (_) {}
    return null;
  }

  function isPublicConfigShape(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value) &&
      Array.isArray(value.timeslots) && Array.isArray(value.collection_timeslots) &&
      Number(value.booking_window) > 0;
  }

  function publicConfigCacheKey(storeKey) {
    return 'wf_ld_public_config_v1:' + storeKey;
  }

  function readCachedPublicConfig(storeKey, diagnostic) {
    function unavailable(reason) {
      if (diagnostic) diagnostic.cacheState = reason;
      return null;
    }
    var storage = getLocalStorage();
    if (!storage) return unavailable('storage_unavailable');
    try {
      var cached = JSON.parse(storage.getItem(publicConfigCacheKey(storeKey)) || 'null');
      if (!cached) return unavailable('missing');
      if (cached.version !== PUBLIC_CONFIG_CACHE_VERSION) return unavailable('version_mismatch');
      if (cached.storeKey !== storeKey) return unavailable('store_mismatch');
      if (!isPublicConfigShape(cached.config) || !Number.isFinite(Number(cached.fetchedAt))) return unavailable('invalid');
      var ageMs = Date.now() - Number(cached.fetchedAt);
      if (ageMs < 0) return unavailable('future_timestamp');
      if (ageMs > PUBLIC_CONFIG_CACHE_TTL_MS) return unavailable('expired');
      if (diagnostic) diagnostic.cacheState = 'valid';
      return { config: cached.config, ageMs: ageMs };
    } catch (_) {
      return unavailable('read_error');
    }
  }

  function configLifecycle() {
    return {
      visibility: typeof document !== 'undefined' && document.visibilityState === 'visible' ? 'visible'
        : typeof document !== 'undefined' && document.visibilityState === 'hidden' ? 'hidden' : 'unknown',
      online: typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    };
  }

  function writeCachedPublicConfig(storeKey, config) {
    var storage = getLocalStorage();
    if (!storage || !isPublicConfigShape(config)) return;
    try {
      storage.setItem(publicConfigCacheKey(storeKey), JSON.stringify({
        version: PUBLIC_CONFIG_CACHE_VERSION,
        storeKey: storeKey,
        fetchedAt: Date.now(),
        config: config,
      }));
    } catch (_) {}
  }

  function timeoutError(timeoutMs) {
    var err = new Error('Request timed out after ' + timeoutMs + 'ms');
    err.name = 'TimeoutError';
    return err;
  }

  function fetchWithTimeout(url, options, timeoutMs, consumeResponse) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var fetchOptions = {};
      Object.keys(options || {}).forEach(function (key) { fetchOptions[key] = options[key]; });
      if (controller) fetchOptions.signal = controller.signal;

      var timer = setTimeout(function () {
        if (settled) return;
        settled = true;
        if (controller) controller.abort();
        reject(timeoutError(timeoutMs));
      }, timeoutMs);

      function settle(callback, value) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        callback(value);
      }

      try {
        // Keep the deadline active through body reads when the caller supplies a consumer.
        Promise.resolve(fetch(url, fetchOptions)).then(function (response) {
          return consumeResponse ? consumeResponse(response) : response;
        }).then(function (response) {
          settle(resolve, response);
        }, function (err) {
          settle(reject, err);
        });
      } catch (err) {
        settle(reject, err);
      }
    });
  }

  function shouldRetryPublicFailure(failure) {
    if (!failure) return false;
    if (failure.reason === 'timeout' || failure.reason === 'fetch_error') return true;
    return failure.reason === 'http_error' && Number(failure.statusCode) >= 500;
  }

  function fetchPublic() {
    var storeKey = getStoreKey();
    var startedAt = nowMs();
    var diagnostics = { version: 1, attempts: [] };
    var firstUrl = buildDeliveryUrl('/public?_t=' + Date.now());
    setLastPublicConfigResult({ ok: false, reason: 'pending', url: firstUrl, elapsedMs: 0, attempts: 0 });

    function recoverOrFail(failure, attempts) {
      var cacheDiagnostic = {};
      var cached = readCachedPublicConfig(storeKey, cacheDiagnostic);
      diagnostics.cacheAtRecovery = cacheDiagnostic.cacheState;
      var result = {
        ok: false,
        reason: failure.reason,
        statusCode: failure.statusCode,
        detail: failure.detail,
        url: failure.url,
        elapsedMs: elapsedSince(startedAt),
        attempts: attempts,
        diagnostics: JSON.parse(JSON.stringify(diagnostics)),
      };
      if (cached) {
        result.recovered = true;
        result.recoverySource = 'local_storage';
        result.cacheAgeMs = Math.round(cached.ageMs);
        setLastPublicConfigResult(result);
        return cached.config;
      }
      setLastPublicConfigResult(result);
      return null;
    }

    function runAttempt(attemptIndex) {
      var attempts = attemptIndex + 1;
      var attemptStarted = nowMs();
      var lifecycle = configLifecycle();
      var diagnostic = { attempt: attempts, startedAt: Date.now(), phase: 'headers',
        visibilityStart: lifecycle.visibility, onlineStart: lifecycle.online };
      var timeoutMs = readCachedPublicConfig(storeKey, diagnostic)
        ? PUBLIC_CONFIG_CACHED_TIMEOUT_MS
        : PUBLIC_CONFIG_ATTEMPT_TIMEOUTS_MS[attemptIndex];
      diagnostic.timeoutMs = timeoutMs;
      var finished = false;
      var url = buildDeliveryUrl('/public?_t=' + diagnostic.startedAt);
      return fetchWithTimeout(url, { credentials: 'omit', cache: 'no-store' }, timeoutMs, function (r) {
        if (!finished) {
          diagnostic.phase = 'body';
          diagnostic.headersMs = elapsedSince(attemptStarted);
          diagnostic.status = r.status;
        }
        if (!r.ok) {
          return r.text().catch(function () { return ''; }).then(function (text) {
            return {
              failure: {
                reason: 'http_error',
                statusCode: r.status,
                detail: String(text || r.statusText || '').slice(0, 500),
                url: url,
              },
            };
          });
        }
        return r.json().then(function (data) {
          if (!isPublicConfigShape(data)) {
            return {
              failure: {
                reason: 'invalid_config',
                statusCode: r.status,
                detail: 'Public config response is missing required fields',
                url: url,
              },
            };
          }
          return { data: data, statusCode: r.status };
        }).catch(function (err) {
          return {
            failure: {
              reason: 'json_parse_error',
              statusCode: r.status,
              detail: describeFetchError(err),
              url: url,
            },
          };
        });
      })
        .catch(function (err) {
          return {
            failure: {
              reason: err && err.name === 'TimeoutError' ? 'timeout' : 'fetch_error',
              detail: describeFetchError(err),
              url: url,
            },
          };
        })
        .then(function (outcome) {
          finished = true;
          diagnostic.elapsedMs = elapsedSince(attemptStarted);
          lifecycle = configLifecycle();
          diagnostic.visibilityEnd = lifecycle.visibility;
          diagnostic.onlineEnd = lifecycle.online;
          if (typeof diagnostic.headersMs === 'number') diagnostic.bodyWaitMs = Math.max(0, diagnostic.elapsedMs - diagnostic.headersMs);
          if (outcome.failure && outcome.failure.reason === 'timeout') diagnostic.timerOverrunMs = Math.max(0, diagnostic.elapsedMs - timeoutMs);
          if (outcome.data) diagnostic.phase = 'complete';
          diagnostics.attempts.push(diagnostic);
          if (outcome.data) {
            writeCachedPublicConfig(storeKey, outcome.data);
            setLastPublicConfigResult({
              ok: true,
              reason: 'ok',
              diagnostics: JSON.parse(JSON.stringify(diagnostics)),
              statusCode: outcome.statusCode,
              url: url,
              elapsedMs: elapsedSince(startedAt),
              attempts: attempts,
            });
            return outcome.data;
          }
          // A valid saved config is already our recovery path; do not delay it with a retry.
          if (readCachedPublicConfig(storeKey)) return recoverOrFail(outcome.failure, attempts);
          if (attemptIndex + 1 < PUBLIC_CONFIG_ATTEMPT_TIMEOUTS_MS.length && shouldRetryPublicFailure(outcome.failure)) {
            return new Promise(function (resolve) { setTimeout(resolve, PUBLIC_CONFIG_RETRY_DELAY_MS); })
              .then(function () { return runAttempt(attemptIndex + 1); });
          }
          return recoverOrFail(outcome.failure, attempts);
        });
    }

    return runAttempt(0);
  }

  function fetchValidatePostal(code, dateIso) {
    if (!code || String(code).length !== 6) return Promise.resolve(null);
    var dateParam = dateIso && /^\d{4}-\d{2}-\d{2}$/.test(dateIso)
      ? '&date=' + encodeURIComponent(dateIso) : '';
    return fetchWithTimeout(buildDeliveryUrl('/validate-postal?code=' + encodeURIComponent(code) + dateParam),
      { credentials: 'omit', cache: 'no-store' }, READ_TIMEOUT_MS, function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (result) {
        return result && typeof result.valid === 'boolean' && typeof result.excluded === 'boolean' ? result : null;
      })
      .catch(function () { return null; });
  }

  function fetchAvailability(dateStr) {
    if (!dateStr || !/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return Promise.resolve(null);
    return fetchWithTimeout(buildDeliveryUrl('/availability?date=' + encodeURIComponent(dateStr) + '&_t=' + Date.now()), { credentials: 'omit', cache: 'no-store' }, READ_TIMEOUT_MS)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  function createCheckout(data) {
    return fetchWithTimeout(buildDeliveryUrl('/checkout'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'omit',
      body: JSON.stringify(data),
    }, CHECKOUT_TIMEOUT_MS)
      .then(function (r) {
        if (r.ok) return r.json();
        return r.json()
          .catch(function () { return { error: r.statusText || 'Checkout failed', statusCode: r.status }; })
          .then(function (err) { return Promise.reject(err); });
      });
  }

  function fetchSeasonal() {
    return fetchWithTimeout(
      API_BASE + '/api/seasonal/public?storeKey=' + encodeURIComponent(getStoreKey()) + '&_t=' + Date.now(),
      { credentials: 'omit', cache: 'no-store' },
      READ_TIMEOUT_MS
    )
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }

  var TELEMETRY_DEDUP_MS = 5 * 60 * 1000;
  var telemetryRecent = {};
  var cartFormRecent = {};

  function getSessionStorage() {
    try {
      if (window.sessionStorage) return window.sessionStorage;
    } catch (_) {}
    try {
      return sessionStorage;
    } catch (_) {}
    return null;
  }

  function parseJson(raw, fallback) {
    if (!raw) return fallback;
    try {
      return JSON.parse(raw);
    } catch (_) {
      return fallback;
    }
  }

  function getAttributionSessionId() {
    var storage = getSessionStorage();
    if (!storage) return '';
    var session = parseJson(storage.getItem(ATTRIBUTION_SESSION_KEY), null);
    if (session && typeof session.sessionId === 'string' && session.sessionId) {
      return session.sessionId;
    }
    return '';
  }

  function getCartFormSessionId() {
    var attributionSessionId = getAttributionSessionId();
    if (attributionSessionId) return attributionSessionId;
    var storage = getSessionStorage();
    if (!storage) return '';
    var existing = String(storage.getItem(CART_FORM_SESSION_KEY) || '').trim();
    if (existing) return existing;
    var generated = 'wf-ld-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
    try {
      storage.setItem(CART_FORM_SESSION_KEY, generated);
    } catch (_) {}
    return generated;
  }

  function normalizeEmail(value) {
    var normalized = String(value || '').trim().toLowerCase();
    return (normalized && normalized.indexOf('@') > 0) ? normalized.slice(0, 320) : '';
  }

  function reportEvent(event, meta) {
    if (!event) return;
    var dedupKey = event + ':' + String((meta && meta.variantId) || '') + ':' + String((meta && meta.reason) || '');
    var now = Date.now();
    if (telemetryRecent[dedupKey] && (now - telemetryRecent[dedupKey]) < TELEMETRY_DEDUP_MS) return;
    telemetryRecent[dedupKey] = now;
    var payload = {
      event: event,
      variantId: (meta && meta.variantId) ? String(meta.variantId) : undefined,
      statusCode: (meta && meta.statusCode) ? Number(meta.statusCode) : undefined,
      reason: (meta && meta.reason) ? String(meta.reason).slice(0, 100) : undefined,
      elapsedMs: (meta && typeof meta.elapsedMs !== 'undefined') ? Number(meta.elapsedMs) : undefined,
      configUrl: (meta && meta.configUrl) ? String(meta.configUrl).slice(0, 500) : undefined,
      detail: (meta && meta.detail) ? String(meta.detail).slice(0, 500) : undefined,
      attempts: (meta && typeof meta.attempts !== 'undefined') ? Number(meta.attempts) : undefined,
      diagnostics: meta && meta.diagnostics ? meta.diagnostics : undefined,
      recoverySource: (meta && meta.recoverySource) ? String(meta.recoverySource).slice(0, 100) : undefined,
      cacheAgeMs: (meta && typeof meta.cacheAgeMs !== 'undefined') ? Number(meta.cacheAgeMs) : undefined,
      page: location.pathname,
      ts: now,
    };
    try {
      fetch(buildDeliveryUrl('/telemetry'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (_) {}
  }

  function reportCartFormInteraction(event, meta) {
    if (!event) return;
    var sessionId = getCartFormSessionId();
    if (!sessionId) return;
    var now = Date.now();
    var normalizedEmail = normalizeEmail(meta && meta.senderEmail);
    var dedupKey = sessionId + ':' + event + ':' + normalizedEmail;
    if (cartFormRecent[dedupKey] && (now - cartFormRecent[dedupKey]) < TELEMETRY_DEDUP_MS) return;
    cartFormRecent[dedupKey] = now;

    var payload = {
      sessionId: sessionId,
      event: String(event),
      page: (typeof location !== 'undefined' && location && location.pathname) ? location.pathname : '/',
      ts: now,
      senderEmail: normalizedEmail || undefined,
      deliveryMethod: (meta && meta.deliveryMethod) ? String(meta.deliveryMethod) : undefined,
    };

    try {
      fetch(buildDeliveryUrl('/cart-form-metrics'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'omit',
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(function () {});
    } catch (_) {}
  }

  setStoreKey(getRuntimeStoreKeyCandidate());

  window.WF_LD_API = {
    API_BASE: API_BASE,
    getStoreKey: getStoreKey,
    setStoreKey: setStoreKey,
    buildDeliveryUrl: buildDeliveryUrl,
    fetchWithTimeout: fetchWithTimeout,
    fetchPublic: fetchPublic,
    getLastPublicConfigResult: getLastPublicConfigResult,
    fetchValidatePostal: fetchValidatePostal,
    fetchAvailability: fetchAvailability,
    createCheckout: createCheckout,
    fetchSeasonal: fetchSeasonal,
    reportEvent: reportEvent,
    reportCartFormInteraction: reportCartFormInteraction,
  };
})();
